import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { Button, Icon, Input } from "semantic-ui-react";
import LeaveManagementTable from "../../components/LeaveManagementTable";
import { clearMessage, getConfigList, updateConfigList } from "../../core/actions/leaveConfigAction";
import functionCommon from "../../core/common/function";
import { commonConstants } from "../../core/constant/common.constants";
import "./styles.scss";

const listLabel = [
    [
        {
            label: "annualLeave",
            name: "Annual leave",
        },
        {
            label: "cutOffDay",
            name: "Cut-off Day",
        },
    ],
    [
        {
            label: "hazardousWork",
            name: "Hazardous work",
        },
        null,
    ],
    [
        {
            label: "seniority",
            name: "Seniority",
        },
        null,
    ],
    [
        {
            label: "carriedOverLeave",
            name: "Carried over leave",
        },
        null,
    ],
    [
        {
            label: "otherAddition",
            name: "Other Addition",
            isDisabled: true,
        },
        null,
    ],
    [
        {
            label: "otherDeduction",
            name: "Other Deduction",
            isDisabled: true,
        },
        null,
    ],
    [
        {
            label: "wedding",
            name: "Special Leave - Wedding",
        },
        null,
    ],
    [
        {
            label: "funeral",
            name: "Special Leave - Funeral",
        },
        null,
    ],
    [
        {
            label: "childCare",
            name: "Special Leave - Child care (under 1 year old)",
        },
        null,
    ],
    [
        {
            label: "unpaidLeave",
            name: "Unpaid Leave",
        },
        null,
    ],
    [
        {
            label: "maternityLeave",
            name: "Social Insurance Leave - Maternity",
        },
        null,
    ],
    [
        {
            label: "paternityLeave",
            name: "Social Insurance Leave - Paternity",
        },
        null,
    ],
    [
        {
            label: "prenatalCheckUpLeave",
            name: "Social Insurance Leave - Prenatal Check-up",
        },
        null,
    ],
    [
        {
            label: "sickLeave",
            name: "Social Insurance Leave - Sickness",
        },
        null,
    ],
    [
        {
            label: "childrenSicknessLeave",
            name: "Social Insurance Leave - Children sickness",
        },
        null,
    ],
];

const InputTable = (props) => {
    const { t } = useTranslation();
    const { value, onChangeValue, label, labelData, disabled, maxNumber } = props;
    const [messageError, setMessageError] = useState(null);

    const handleChangeValue = (event) => {
        const newValue = functionCommon.onInputNumberPositive(event);
        const max = maxNumber ? maxNumber : commonConstants.NUMBER_DAY_IN_YEAR;
        if (!isNaN(Number(newValue)) && Number(newValue) <= max && Number(newValue) > 0)
            onChangeValue(labelData, newValue);
        else if (newValue === "") {
            onChangeValue(labelData, "");
        }
    };

    const onBlur = (event) => {
        if (!isNaN(parseInt(event.target.value))) onChangeValue(labelData, parseInt(event.target.value));
        else onChangeValue(labelData, "");
    };

    useEffect(() => {
        if (!value && String(value) !== "0") {
            setMessageError(t("message.029", { field: label }));
        } else {
            setMessageError(null);
        }
    }, [value, t]);

    return (
        <div className="input-cell">
            <Input
                min="0"
                value={value}
                type="text"
                onChange={handleChangeValue}
                disabled={disabled}
                error={messageError !== null}
                onKeyDown={functionCommon.onKeyDownNumber}
                onPaste={functionCommon.onPasteNumber}
                onBlur={onBlur}
            />
            {messageError && <span className="error-message">{messageError}</span>}
        </div>
    );
};

export default function LeaveManagementConfig() {
    const [initData, setInitData] = useState({});
    const [isDisableUpdate, setIsDisableUpdate] = useState(true);
    const dispatch = useDispatch();
    const leaveConfigStore = useSelector((state) => state.leaveConfigReducer);
    const { updating, successCode, errorCode } = leaveConfigStore;
    const { t } = useTranslation();
    const dataRef = useRef();
    const [message, setMessage] = useState(null);

    const handleChangeData = (label, value) => {
        // Check empty + format value to Number if type of value is number
        setInitData({
            ...initData,
            [label]: Number(value) || value === 0 ? Number(value) : String(value),
        });
    };

    const handleChangeRenewDay = (value) => {
        setInitData({ ...initData, renewDay: value });
    };

    const triggerMessage = () => {
        setTimeout(() => {
            setMessage(null);
            dispatch(clearMessage());
        }, 2000);
    };

    const dataConfig = useMemo(() => {
        return {
            header: [t("labels.leaveType"), t("labels.maxDays"), t("labels.cutOffDay")],
            body:
                Object.keys(initData).length !== 0
                    ? listLabel.map((parentEle, parentIdx) => {
                          const firstItem = parentEle[0].name;
                          const sencondItem = (
                              <InputTable
                                  label={parentEle[0].name}
                                  labelData={parentEle[0].label}
                                  value={initData[parentEle[0].label]}
                                  disabled={parentEle[0].isDisabled}
                                  onChangeValue={handleChangeData}
                              />
                          );
                          const thirdItem = parentEle[1] ? (
                              <InputTable
                                  label={parentEle[1].name}
                                  labelData={parentEle[1].label}
                                  value={initData[parentEle[1].label]}
                                  disabled={parentEle[1].isDisabled}
                                  onChangeValue={handleChangeData}
                                  maxNumber={parentEle[1].label === "cutOffDay" ? 28 : null}
                              />
                          ) : null;
                          return [firstItem, sencondItem, thirdItem];
                      })
                    : [],
        };
    }, [initData, t]);

    const onUpdate = () => {
        dispatch(updateConfigList(initData));
    };

    useEffect(() => {
        if (successCode) {
            setMessage({ text: `message.${successCode}`, isError: false });
            triggerMessage();
        }
    }, [successCode]);

    useEffect(() => {
        if (errorCode) {
            setMessage({ text: `message.${errorCode}`, isError: true });
            triggerMessage();
        }
    }, [errorCode]);

    useEffect(() => {
        const valid = Object.values(initData).findIndex((ele, index) => !ele && index !== 4 && index !== 5) >= 0;
        if (JSON.stringify(initData) === JSON.stringify(dataRef.current) || valid) {
            setIsDisableUpdate(true);
        } else {
            setIsDisableUpdate(false);
        }
    }, [initData]);

    useEffect(() => {
        dispatch(getConfigList());
    }, []);

    useEffect(() => {
        if (leaveConfigStore.listConfig) {
            setInitData(leaveConfigStore.listConfig);
            dataRef.current = leaveConfigStore.listConfig;
        }
    }, [leaveConfigStore.listConfig]);

    return (
        <div className="leave-manage-config">
            <h2 className="page-heading">
                <span onClick={functionCommon.goToPage("/my-leave-request")}>{t("labels.leaveManagemnet")}</span>
                {"/ "}
                <span onClick={functionCommon.goToPage("/config")}>{t("labels.config")}</span>
            </h2>
            <div className="form-wrapper">
                <Button className="leave-button" onClick={onUpdate} disabled={isDisableUpdate || updating}>
                    <Icon name="edit" />
                    {updating ? t("button.updating") : t("button.update")}
                </Button>
            </div>
            {message && (
                <p className={`${message.isError ? "error-message" : "success-message"} list-error-message`}>
                    {t(message.text)}
                </p>
            )}
            <LeaveManagementTable content={dataConfig} customTableClass="table-custom" />
        </div>
    );
}
